import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { WarningCircle, MapTrifold, X, Funnel, CheckCircle } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import RouteMapModal from "../components/RouteMapModal";

const ANOMALY_OPTIONS = [
  { v: "all", l: "All trips" },
  { v: "any", l: "Any anomaly" },
  { v: "speed_excess", l: "Speed excess" },
  { v: "distance_excess", l: "Distance excess" },
  { v: "monthly_cap_breached", l: "Monthly cap breached" },
];
const STATUS_OPTIONS = [
  { v: "all", l: "All statuses" },
  { v: "active", l: "Active" },
  { v: "completed", l: "Completed" },
  { v: "claimed", l: "Claimed" },
];

export default function TripReview() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [anomaly, setAnomaly] = useState("all");
  const [status, setStatus] = useState("all");
  const [mapTrip, setMapTrip] = useState(null);

  const load = () => {
    api.get("/trips").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (anomaly !== "all") {
        const codes = (t.anomalies || []).map((a) => a.code);
        if (anomaly === "any" && codes.length === 0) return false;
        if (anomaly !== "any" && !codes.includes(anomaly)) return false;
      }
      if (qq) {
        const hay = [t.employee_name, t.employee_code, t.start_address, t.end_address, t.purpose, t.vehicle_no]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [rows, anomaly, status, q]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const anomalyCount = filtered.filter((t) => (t.anomalies || []).length > 0).length;
    const totalKm = filtered.reduce((s, t) => s + (t.distance_km || 0), 0);
    const totalAmt = filtered.reduce((s, t) => s + (t.suggested_amount || 0), 0);
    return { total, anomalyCount, totalKm: totalKm.toFixed(1), totalAmt: totalAmt.toFixed(0) };
  }, [filtered]);

  // Eligible for one-click bulk approve: completed, no anomalies, not yet claimed, has an amount
  const eligibleForBulk = useMemo(
    () => filtered.filter((t) =>
      t.status === "completed"
      && (t.anomalies || []).length === 0
      && !t.claim_id
      && (t.suggested_amount || 0) > 0
    ),
    [filtered]
  );
  const bulkAmount = eligibleForBulk.reduce((s, t) => s + (t.suggested_amount || 0), 0);
  const [bulkBusy, setBulkBusy] = useState(false);

  const runBulkApprove = async () => {
    if (!eligibleForBulk.length) return;
    if (!window.confirm(`Auto-draft fuel claims for ${eligibleForBulk.length} trip(s) totalling ₹${bulkAmount.toFixed(0)}?\nEmployees will still need to attach receipts and submit each draft.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/trips/bulk-to-claim", { trip_ids: eligibleForBulk.map((t) => t.id) });
      const msg = `Drafted ${data.created_count} claim(s) · ₹${data.total_amount}${data.skipped_count ? ` · ${data.skipped_count} skipped (no fuel rate set)` : ""}`;
      toast.success(msg, { duration: 5000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBulkBusy(false);
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal">Field ops · HR/Manager</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy">Trip review</h1>
        <p className="text-sm text-zinc-500 mt-2">Every field trip across your team. Filter by anomaly, replay the route on the map, and approve claims in bulk.</p>
      </div>

      {/* One-click bulk approve card */}
      {eligibleForBulk.length > 0 && (
        <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="bulk-approve-card">
          <CheckCircle size={24} weight="fill" className="text-emerald-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-900">
              {eligibleForBulk.length} no-anomaly trip{eligibleForBulk.length === 1 ? "" : "s"} ready to auto-draft
            </div>
            <div className="text-xs text-emerald-800 mt-0.5">
              Combined fuel claim ₹{bulkAmount.toFixed(0)} across {new Set(eligibleForBulk.map((t) => t.employee_id)).size} employee(s). Employees still need to attach receipts before submission.
            </div>
          </div>
          <Button onClick={runBulkApprove} disabled={bulkBusy} className="btn-zenith text-white rounded-sm" data-testid="bulk-approve-btn">
            {bulkBusy ? "Drafting…" : `Auto-draft ${eligibleForBulk.length} claim(s)`}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Trips" value={stats.total} />
        <StatCard label="With anomalies" value={stats.anomalyCount} danger={stats.anomalyCount > 0} />
        <StatCard label="Total km" value={`${stats.totalKm} km`} />
        <StatCard label="Est. amount" value={`₹ ${Number(stats.totalAmt).toLocaleString("en-IN")}`} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 items-center sticky top-0 z-10 bg-white/85 backdrop-blur py-2 border-y border-zinc-200 -mx-4 px-4">
        <Funnel size={14} className="text-zinc-500" />
        <Input placeholder="Search employee / address / vehicle…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs rounded-sm h-9" data-testid="trip-review-search" />
        <Select value={anomaly} onValueChange={setAnomaly}>
          <SelectTrigger className="w-52 rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{ANOMALY_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Date</th><th>From → To</th><th>Mode</th><th>Km</th><th>Amount</th><th>Anomalies</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-zinc-500 text-sm">No trips match these filters.</td></tr>}
            {filtered.map((t) => (
              <tr key={t.id}>
                <td>
                  <div className="text-sm">{t.employee_name}</div>
                  <div className="font-mono-plex text-xs text-zinc-500">{t.employee_code}</div>
                </td>
                <td className="font-mono-plex text-xs">{(t.start_at || "").slice(0, 10)}</td>
                <td className="text-xs max-w-[240px]">
                  <div className="truncate" title={t.start_address_full || ""}>{t.start_address || "—"}</div>
                  <div className="text-zinc-500 truncate" title={t.end_address_full || ""}>→ {t.end_address || "—"}</div>
                </td>
                <td className="text-xs uppercase">{t.mode}</td>
                <td className="font-mono-plex text-xs">{t.distance_km ?? "—"}</td>
                <td className="font-mono-plex text-xs">{t.suggested_amount != null ? `₹ ${t.suggested_amount}` : "—"}</td>
                <td>
                  {t.anomalies?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {t.anomalies.map((a) => (
                        <span key={a.code} className="text-[10px] px-1.5 py-0.5 bg-orange-50 border border-orange-200 text-orange-700 rounded-sm inline-flex items-center gap-1" title={a.detail}>
                          <WarningCircle size={10} weight="fill" /> {a.code.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-zinc-400 text-xs">—</span>}
                </td>
                <td><Badge variant="outline" className={`rounded-sm ${t.status === "claimed" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : t.status === "completed" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{t.status}</Badge></td>
                <td>
                  <button onClick={() => setMapTrip(t)} className="text-xs px-2 py-1 border border-adb-navy/30 text-adb-navy hover:bg-adb-navy/5 rounded-sm inline-flex items-center gap-1" data-testid={`review-map-${t.id}`}>
                    <MapTrifold size={12} /> Map
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mapTrip && <RouteMapModal trip={mapTrip} onClose={() => setMapTrip(null)} />}
    </div>
  );
}

function StatCard({ label, value, danger }) {
  return (
    <div className={`border rounded-sm p-4 bg-white ${danger ? "border-orange-300" : "border-zinc-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${danger ? "text-orange-700" : "text-adb-navy"}`}>{value}</div>
    </div>
  );
}
